import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Input, Select, Checkbox } from 'antd';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../interceptors/api';
import StandardButton from '../components/StandardButton';
import ProtectedToolWrapper from '../components/ProtectedToolWrapper';

const { Option } = Select;

const YoutubeToMP3 = () => {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [format, setFormat] = useState('mp3'); // 'mp3' or 'mp4'
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [silent, setSilent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const extractVideoId = (url) => {
    const match = url.match(
      /(?:youtube\.com\/(?:shorts\/|(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=))|youtu\.be\/)([^"&?/ ]{11})/
    );
    return match ? match[1] : null;
  };

  const parseTimeToSeconds = (timeStr) => {
    if (!timeStr || !timeStr.trim()) return null;
    const parts = timeStr.trim().split(':').map((p) => Number(p));
    if (parts.some((n) => Number.isNaN(n) || n < 0)) return NaN;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 1) return parts[0];
    return NaN;
  };

  const handleDownload = async () => {
    const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
    if (!userProfileData?.id) {
      toast.error(t('login_required') || 'Please log in to use this feature');
      return;
    }

    const videoId = extractVideoId(youtubeUrl);
    if (!videoId) {
      return toast.error(t('please_enter_valid_youtube_url'));
    }

    const startSec = parseTimeToSeconds(startTime);
    const endSec = parseTimeToSeconds(endTime);

    if (format === 'mp4') {
      if (startTime && Number.isNaN(startSec)) {
        return toast.error(t('yt_trim_invalid_time'));
      }
      if (endTime && Number.isNaN(endSec)) {
        return toast.error(t('yt_trim_invalid_time'));
      }
      if (startSec != null && endSec != null && !Number.isNaN(startSec) && !Number.isNaN(endSec) && endSec <= startSec) {
        return toast.error(t('yt_trim_end_before_start'));
      }
    }

    setLoading(true);
    const toastId = toast.loading(
      format === 'mp3' ? t('downloading_mp3') : t('downloading_mp4')
    );

    try {
      const body = { yt_id: videoId, format };
      if (format === 'mp4') {
        if (startSec != null && !Number.isNaN(startSec)) body.startTime = startSec;
        if (endSec != null && !Number.isNaN(endSec)) body.endTime = endSec;
        if (silent) body.silent = true;
      }
      // Use the unified endpoint, passing the selected format
      const response = await api.post(
        '/youtube-download',
        body,
        { responseType: 'blob' }
      );

      // Untrimmed downloads: the backend returns a direct link to the office
      // API and the browser fetches from there itself (native progress bar,
      // nothing buffered in memory). Trimmed downloads still stream through
      // the backend as a blob.
      const responseType = response.headers?.['content-type'] || '';
      if (responseType.includes('application/json')) {
        const { download_url: downloadUrl } = JSON.parse(await response.data.text());
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success(t('download_started'));
        return;
      }

      const contentType = format === 'mp3' ? 'audio/mpeg' : 'video/mp4';
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${videoId}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      toast.success(t('download_success'));
    } catch (err) {
      // With responseType "blob" the JSON error body arrives as a Blob.
      let serverError = '';
      const data = err?.response?.data;
      if (data) {
        try {
          const raw = typeof data === 'string' ? data : await data.text();
          serverError = JSON.parse(raw)?.error || '';
        } catch {
          // Non-JSON body — fall through to the generic message.
        }
      }
      toast.error(serverError || t('download_failed'));
    } finally {
      toast.dismiss(toastId);
      setLoading(false);
    }
  };

  return (
    <ProtectedToolWrapper>
      <div className="max-w-5xl mx-auto px-4">
        <Card className="p-6 rounded-xl shadow-lg space-y-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <Input
              placeholder={t('enter_youtube_url_placeholder')}
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              className="flex-1"
              size="large"
            />
            <Select
              value={format}
              onChange={setFormat}
              size="large"
              style={{ width: 120 }}
            >
              <Option value="mp3">MP3 (Audio)</Option>
              <Option value="mp4">MP4 (Video)</Option>
            </Select>
            <StandardButton
              onClick={handleDownload}
              loading={loading}
              type="secondary"
              size="large"
              className="w-full md:w-auto"
            >
              {t('download_button')}
            </StandardButton>
          </div>
          {format === 'mp4' && (
            <>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <Input
                  placeholder={t('yt_trim_start_placeholder')}
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  size="large"
                  style={{ maxWidth: 200 }}
                />
                <Input
                  placeholder={t('yt_trim_end_placeholder')}
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  size="large"
                  style={{ maxWidth: 200 }}
                />
                <span className="text-xs text-gray-500">{t('yt_trim_hint')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={silent}
                  onChange={(e) => setSilent(e.target.checked)}
                >
                  {t('yt_silent_label')}
                </Checkbox>
                <span className="text-xs text-gray-500">{t('yt_silent_hint')}</span>
              </div>
            </>
          )}
        </Card>
      </div>
    </ProtectedToolWrapper>
  );
};

export default YoutubeToMP3;